import React, { useEffect, useMemo, useState } from "react";
import API from "../../../services/api";
import SaveStudioAssetButton from "../SaveStudioAssetButton";
import FieldHint from "../FieldHint";
import useI18n from "../../../i18n/useI18n";

import MotionSurface from "../../../motion/studio/MotionSurface";
import ToolResultSkeleton from "../../loaders/ToolResultSkeleton";

import ResultCard from "../../ui/ResultCard";
import ActionRow from "../../ui/ActionRow";
import CopyButton from "../../ui/CopyButton";

import AIActionButton from "../../ui/buttons/AIActionButton";
import UtilityButton from "../../ui/buttons/UtilityButton";

const STUDIO_USAGE_REFRESH_KEY = "hookora_studio_usage_refresh";
const THEME_KEY = "hookora_theme";

const getPreferredLanguage = () => {
  if (typeof window === "undefined") return "en";
  return localStorage.getItem("hookora_language") || "en";
};

const notifyStudioUsageUpdated = () => {
  const value = String(Date.now());
  localStorage.setItem(STUDIO_USAGE_REFRESH_KEY, value);
  window.dispatchEvent(new CustomEvent("hookora:studio-usage-updated"));
};

const normalizeTags = (tags) => {
  if (!Array.isArray(tags)) return [];
  return tags
    .map((tag) => String(tag || "").trim())
    .filter(Boolean);
};

const getScoreTone = (score) => {
  const safe = Number(score) || 0;
  if (safe >= 85) {
    return {
      bar: "#16a34a",
      pillBg: "rgba(22,163,74,0.14)",
      pillText: "#16a34a",
    };
  }
  if (safe >= 70) {
    return {
      bar: "#2563eb",
      pillBg: "rgba(37,99,235,0.14)",
      pillText: "#2563eb",
    };
  }
  return {
    bar: "#f59e0b",
    pillBg: "rgba(245,158,11,0.16)",
    pillText: "#b45309",
  };
};

const HashtagTool = ({
  sharedTopic,
  setSharedTopic,
  sharedPlatform,
  setSharedPlatform,
}) => {
  const { t } = useI18n();

  const getStoredTheme = () => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(THEME_KEY) || "light";
  };

  const [audience, setAudience] = useState("");
  const [goal, setGoal] = useState("");
  const [contentType, setContentType] = useState("");
  const [keywords, setKeywords] = useState("");
  const [brandVoice, setBrandVoice] = useState("");
  const [extraContext, setExtraContext] = useState("");

  const [sets, setSets] = useState([]);
  const [error, setError] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [theme, setTheme] = useState(getStoredTheme());

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const syncTheme = () => setTheme(localStorage.getItem(THEME_KEY) || "light");
    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-preferences-updated", syncTheme);
    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-preferences-updated", syncTheme);
    };
  }, []);

  const isDark = theme === "dark";

  const colors = useMemo(
    () => ({
      cardBg: isDark ? "rgba(15,23,42,0.78)" : "#ffffff",
      cardBgStrong: isDark ? "rgba(15,23,42,0.92)" : "#ffffff",
      border: isDark ? "rgba(148,163,184,0.22)" : "#e5e7eb",
      borderStrong: isDark ? "rgba(148,163,184,0.34)" : "#d1d5db",
      text: isDark ? "#f8fafc" : "#111827",
      textMuted: isDark ? "#94a3b8" : "#6b7280",
      textSoft: isDark ? "#cbd5e1" : "#374151",
      inputBg: isDark ? "#0f172a" : "#ffffff",
      inputText: isDark ? "#f8fafc" : "#111827",
      trackBg: isDark ? "#1e293b" : "#e5e7eb",
      panelBg: isDark ? "rgba(30,41,59,0.55)" : "#f8fafc",
      chipBg: isDark ? "rgba(30,41,59,0.9)" : "#f1f5f9",
      chipText: isDark ? "#cbd5e1" : "#475569",
      buttonBg: isDark ? "#e2e8f0" : "#111827",
      buttonText: isDark ? "#020617" : "#ffffff",
      secondaryButtonBg: isDark ? "#1e293b" : "#e5e7eb",
      secondaryButtonText: isDark ? "#f8fafc" : "#111827",
      dangerBg: isDark ? "rgba(127,29,29,0.25)" : "#fef2f2",
      dangerText: isDark ? "#fecaca" : "#b91c1c",
      label: isDark ? "#e2e8f0" : "#111827",
      heroBg: isDark
        ? "linear-gradient(135deg, rgba(30,41,59,0.84), rgba(15,23,42,0.96))"
        : "linear-gradient(135deg, #f8fafc, #ffffff)",
      heroBorder: isDark ? "rgba(59,130,246,0.24)" : "#bfdbfe",
      heroBadgeBg: isDark ? "rgba(37,99,235,0.14)" : "#eff6ff",
      heroBadgeText: isDark ? "#bfdbfe" : "#1d4ed8",
      heroBadgeBorder: isDark ? "rgba(96,165,250,0.24)" : "#bfdbfe",
      shadow: isDark
        ? "0 16px 34px rgba(2,6,23,0.34)"
        : "0 12px 28px rgba(15,23,42,0.06)",
      shadowSoft: isDark
        ? "0 10px 24px rgba(2,6,23,0.22)"
        : "0 6px 18px rgba(15,23,42,0.05)",
    }),
    [isDark]
  );

  const inputStyle = {
    width: "100%",
    padding: "13px 14px",
    borderRadius: "12px",
    border: `1px solid ${colors.borderStrong}`,
    outline: "none",
    boxSizing: "border-box",
    background: colors.inputBg,
    color: colors.inputText,
    fontSize: "14px",
  };

  const textareaStyle = {
    ...inputStyle,
    minHeight: "86px",
    resize: "vertical",
  };

  const labelStyle = {
    display: "flex",
    alignItems: "center",
    marginBottom: "6px",
    color: colors.label,
    fontSize: "14px",
    fontWeight: 600,
  };

  const clearResults = () => {
    setSets([]);
    setCopiedId(null);
    setError("");
  };

  const resetForm = () => {
    setSharedTopic("");
    setSharedPlatform("Instagram");
    setAudience("");
    setGoal("");
    setContentType("");
    setKeywords("");
    setBrandVoice("");
    setExtraContext("");
    setSets([]);
    setCopiedId(null);
    setError("");
  };

  const generateHashtags = async () => {
    if (!sharedTopic.trim()) {
      setError(t("studioTools.hashtagTool.errors.enterTopic", "Please enter a topic first."));
      return;
    }

    try {
      setLoading(true);
      setError("");

      const response = await API.post("/studio/hashtags", {
        topic: sharedTopic.trim(),
        platform: sharedPlatform,
        audience,
        goal,
        content_type: contentType,
        keywords,
        brand_voice: brandVoice,
        extra_context: extraContext,
        language: getPreferredLanguage(),
      });

      setSets(Array.isArray(response?.data?.sets) ? response.data.sets : []);
      setCopiedId(null);
      notifyStudioUsageUpdated();
    } catch (err) {
      console.error("Failed to generate hashtags:", err);
      setError(
        err?.response?.data?.detail ||
          t("studioTools.hashtagTool.errors.generateFailed", "Failed to generate hashtags.")
      );
    } finally {
      setLoading(false);
    }
  };

  const copySet = async (tags, id) => {
    try {
      await navigator.clipboard.writeText(normalizeTags(tags).join(" "));
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error("Failed to copy hashtag set:", err);
    }
  };

  const renderScoreBar = (score) => {
    const safe = Number(score) || 0;
    const tone = getScoreTone(safe);
    return (
      <div
        style={{
          marginTop: "10px",
          height: "10px",
          borderRadius: "999px",
          background: colors.trackBg,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${Math.max(0, Math.min(100, safe))}%`,
            height: "100%",
            borderRadius: "999px",
            background: tone.bar,
          }}
        />
      </div>
    );
  };

  return (
    <MotionSurface>
    <div style={{ display: "grid", gap: "18px" }}>
      <div
        style={{
          border: `1px solid ${colors.heroBorder}`,
          borderRadius: "20px",
          padding: "18px",
          background: colors.heroBg,
          boxShadow: colors.shadow,
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            padding: "6px 10px",
            borderRadius: "999px",
            background: colors.heroBadgeBg,
            color: colors.heroBadgeText,
            border: `1px solid ${colors.heroBadgeBorder}`,
            fontSize: "12px",
            fontWeight: 800,
            letterSpacing: "0.03em",
            marginBottom: "12px",
          }}
        >
          {t("studioTools.hashtagTool.hero.badge", "Discovery tool")}
        </div>

        <div style={{ color: colors.text, fontSize: "24px", fontWeight: 800, marginBottom: "8px" }}>
          {t("studioTools.hashtagTool.hero.title", "Hashtags")}
        </div>

        <div style={{ color: colors.textSoft, lineHeight: "1.7", maxWidth: "880px" }}>
          {t(
            "studioTools.hashtagTool.hero.description",
            "Use this tool to build strategic hashtag sets for different content goals. Add platform, audience, content type, and extra context so Hookora can return better hashtag mixes for discovery, relevance, and engagement."
          )}
        </div>
      </div>

      <div
        style={{
          border: `1px solid ${colors.border}`,
          borderRadius: "18px",
          padding: "18px",
          background: colors.cardBg,
          boxShadow: colors.shadowSoft,
        }}
      >
        <div style={{ display: "grid", gap: "14px" }}>
          <div>
            <label style={labelStyle}>
              {t("studioTools.hashtagTool.fields.topic.label", "Topic")}
              <FieldHint
                text={t(
                  "studioTools.hashtagTool.fields.topic.hint",
                  "The subject, niche, or campaign the hashtag sets should support."
                )}
              />
            </label>
            <textarea
              value={sharedTopic}
              onChange={(e) => setSharedTopic(e.target.value)}
              placeholder={t(
                "studioTools.hashtagTool.fields.topic.placeholder",
                "What is the hashtag set for?"
              )}
              style={textareaStyle}
            />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "12px",
            }}
          >
            <div>
              <label style={labelStyle}>
                {t("studioTools.hashtagTool.fields.platform.label", "Platform")}
                <FieldHint
                  text={t(
                    "studioTools.hashtagTool.fields.platform.hint",
                    "Choose the platform because hashtag behavior differs by platform."
                  )}
                />
              </label>
              <select
                value={sharedPlatform}
                onChange={(e) => setSharedPlatform(e.target.value)}
                style={inputStyle}
              >
                {[
                  "Instagram",
                  "Instagram Reels",
                  "TikTok",
                  "X",
                  "LinkedIn",
                  "Facebook",
                  "YouTube Shorts",
                ].map((platform) => (
                  <option key={platform} value={platform}>
                    {platform}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>
                {t("studioTools.hashtagTool.fields.contentType.label", "Content Type")}
                <FieldHint
                  text={t(
                    "studioTools.hashtagTool.fields.contentType.hint",
                    "The type of post or content. Example: tutorial, product demo, educational post, personal story, carousel."
                  )}
                />
              </label>
              <input
                value={contentType}
                onChange={(e) => setContentType(e.target.value)}
                placeholder={t(
                  "studioTools.hashtagTool.fields.contentType.placeholder",
                  "Content type"
                )}
                style={inputStyle}
              />
            </div>
          </div>

          <div
            style={{
              padding: "14px",
              borderRadius: "14px",
              background: colors.panelBg,
              border: `1px solid ${colors.border}`,
            }}
          >
            <div style={{ fontWeight: 700, color: colors.text, marginBottom: "12px" }}>
              {t("studioTools.hashtagTool.contextTitle", "Hashtag Context")}
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: "12px",
              }}
            >
              <div>
                <label style={labelStyle}>
                  {t("studioTools.hashtagTool.fields.audience.label", "Audience")}
                  <FieldHint
                    text={t(
                      "studioTools.hashtagTool.fields.audience.hint",
                      "Who you want to attract with these hashtags. Example: beauty lovers, startup founders, student creators."
                    )}
                  />
                </label>
                <input
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  placeholder={t("studioTools.hashtagTool.fields.audience.placeholder", "Audience")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("studioTools.hashtagTool.fields.goal.label", "Goal")}
                  <FieldHint
                    text={t(
                      "studioTools.hashtagTool.fields.goal.hint",
                      "What you want the hashtag set to help with. Example: discovery, relevance, engagement, niche reach."
                    )}
                  />
                </label>
                <input
                  value={goal}
                  onChange={(e) => setGoal(e.target.value)}
                  placeholder={t("studioTools.hashtagTool.fields.goal.placeholder", "Goal")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("studioTools.hashtagTool.fields.keywords.label", "Keywords")}
                  <FieldHint
                    text={t(
                      "studioTools.hashtagTool.fields.keywords.hint",
                      "Important topic words to reflect in the hashtags. Separate them with commas."
                    )}
                  />
                </label>
                <input
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                  placeholder={t("studioTools.hashtagTool.fields.keywords.placeholder", "Keywords")}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>
                  {t("studioTools.hashtagTool.fields.brandVoice.label", "Brand Voice")}
                  <FieldHint
                    text={t(
                      "studioTools.hashtagTool.fields.brandVoice.hint",
                      "The vibe of the brand or content. Helps keep the hashtag sets aligned with the content style."
                    )}
                  />
                </label>
                <input
                  value={brandVoice}
                  onChange={(e) => setBrandVoice(e.target.value)}
                  placeholder={t(
                    "studioTools.hashtagTool.fields.brandVoice.placeholder",
                    "Brand voice"
                  )}
                  style={inputStyle}
                />
              </div>
            </div>

            <div style={{ marginTop: "12px" }}>
              <label style={labelStyle}>
                {t("studioTools.hashtagTool.fields.extraContext.label", "Extra Context")}
                <FieldHint
                  text={t(
                    "studioTools.hashtagTool.fields.extraContext.hint",
                    "Add subtopic details, campaign context, audience specifics, or niche notes to improve the sets."
                  )}
                />
              </label>
              <textarea
                value={extraContext}
                onChange={(e) => setExtraContext(e.target.value)}
                placeholder={t(
                  "studioTools.hashtagTool.fields.extraContext.placeholder",
                  "Extra context, niche, subtopic, or campaign notes"
                )}
                style={textareaStyle}
              />
            </div>
          </div>

          {error && (
            <div
              style={{
                padding: "12px 14px",
                borderRadius: "12px",
                background: colors.dangerBg,
                color: colors.dangerText,
              }}
            >
              {error}
            </div>
          )}

          <ActionRow>
            <AIActionButton isDark={isDark}
              onClick={generateHashtags}
              disabled={loading}
            >
              {loading
                ? t("studioTools.hashtagTool.actions.generating", "Generating...")
                : t("studioTools.hashtagTool.actions.generate", "Generate Hashtag Sets")}
            </AIActionButton>

            <UtilityButton isDark={isDark}
              type="button"
              onClick={clearResults}
            >
              {t("studioTools.hashtagTool.actions.clearResults", "Clear Results")}
            </UtilityButton>

            <UtilityButton isDark={isDark}
              type="button"
              onClick={resetForm}
            >
              {t("studioTools.hashtagTool.actions.resetForm", "Reset Form")}
            </UtilityButton>
          </ActionRow>
        </div>
      </div>

      {!!sets.length && (
        <div
          style={{
            color: colors.textMuted,
            fontSize: "14px",
            lineHeight: "1.6",
            marginTop: "-4px",
          }}
        >
          {t(
            "studioTools.hashtagTool.resultsHelper",
            "These are ready-to-use hashtag sets. Pick the one that best matches your content goal and platform behavior."
          )}
        </div>
      )}

      <div style={{ display: "grid", gap: "14px" }}>
        {loading ? (
          <ToolResultSkeleton isDark={isDark} count={3} />
        ) : (
        sets.map((set, index) => {
          const safeTags = normalizeTags(set.tags);
          const safeScore = Number(set.score) || 0;
          const tone = getScoreTone(safeScore);

          return (
            <ResultCard
              key={`${set.title}-${index}`}
              colors={colors}
              index={index}
            >
              <div style={{ color: colors.text, fontWeight: 700, fontSize: "18px" }}>{set.title}</div>
              <div style={{ color: colors.textMuted, marginTop: "6px", lineHeight: "1.6" }}>
                {set.description}
              </div>

              <div
                style={{
                  display: "inline-flex",
                  marginTop: "10px",
                  background: tone.pillBg,
                  color: tone.pillText,
                  borderRadius: "999px",
                  padding: "6px 10px",
                  fontSize: "12px",
                  fontWeight: 700,
                }}
              >
                {t("studioTools.hashtagTool.scoreLabel", "Score")}: {safeScore}
              </div>

              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "14px" }}>
                {safeTags.map((tag) => (
                  <span
                    key={tag}
                    style={{
                      background: colors.chipBg,
                      borderRadius: "999px",
                      padding: "7px 10px",
                      fontSize: "12px",
                      color: colors.chipText,
                      fontWeight: 600,
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>

              {renderScoreBar(safeScore)}

              <ActionRow style={{ marginTop: "14px" }}>
                <CopyButton
                  copied={copiedId === index}
                  onClick={() => copySet(safeTags, index)}
                >
                  {copiedId === index
                    ? t("studioTools.hashtagTool.actions.copied", "Copied")
                    : t("studioTools.hashtagTool.actions.copy", "Copy")}
                </CopyButton>

                <SaveStudioAssetButton
                  toolType="hashtags"
                  title={`${set?.title || `${t("studioTools.hashtagTool.assetTitlePrefix", "Hashtag Set")} ${index + 1}`} • ${sharedTopic || t("studioTools.hashtagTool.untitledTopic", "Untitled Topic")}`}
                  topic={sharedTopic}
                  platform={sharedPlatform}
                  content={safeTags.join(" ")}
                  meta={{
                    score: set?.score ?? 0,
                    label: set?.label || "",
                    description: set?.description || "",
                    tag_count: safeTags.length,
                  }}
                />
              </ActionRow>
            </ResultCard>
          );
        })
        )}
      </div>
    </div>
    </MotionSurface>
  );
};

export default HashtagTool;